// NEXTSCENES · CHILDREN'S BOOK V1E3 · navigation-only candidate.
// Input is the established buildChildrensBookChapterSceneNavigationV1 projection.
// Never persists, creates scenes, rewrites pages, or calls Novel/Canon APIs.

const clean = (value) => String(value ?? "").trim();
const failed = (code) => ({ ok: false, code });

function readIdentityIndex(navigation) {
  if (navigation?.ok !== true || !Array.isArray(navigation.sections)) {
    return failed("invalid_navigation_projection");
  }

  const chapters = new Map();
  const scenes = new Map();
  const units = new Map();
  const addUnit = (entry, context) => {
    const unitId = clean(entry?.unitId);
    if (!unitId || units.has(unitId)) return false;
    units.set(unitId, {
      unitId,
      pageLabel: clean(entry?.pageLabel),
      title: clean(entry?.title),
      sourceIndex: entry?.sourceIndex,
      pageStart: entry?.pageStart,
      pageEnd: entry?.pageEnd,
      ...context,
    });
    return true;
  };

  for (const section of navigation.sections) {
    const kind = clean(section?.kind);
    if (kind === "chapter") {
      const chapterId = clean(section?.key);
      if (!chapterId || chapters.has(chapterId) || !Array.isArray(section.scenes)) {
        return failed("invalid_chapter_identity");
      }
      chapters.set(chapterId, { chapterId, title: clean(section.title) });
      for (const scene of section.scenes) {
        const sceneId = clean(scene?.key);
        if (!sceneId || scenes.has(sceneId) || !Array.isArray(scene.entries) || !scene.entries.length) {
          return failed("invalid_scene_identity");
        }
        const sceneUnitIds = [];
        for (const entry of scene.entries) {
          if (!addUnit(entry, { chapterId, sceneId, sectionKind: "chapter" })) {
            return failed("duplicate_or_missing_unit_identity");
          }
          sceneUnitIds.push(clean(entry.unitId));
        }
        scenes.set(sceneId, { chapterId, sceneId, title: clean(scene.title), unitIds: sceneUnitIds });
      }
    } else if (["front", "back", "unassigned"].includes(kind)) {
      if (!Array.isArray(section.entries)) return failed("invalid_section_entries");
      for (const entry of section.entries) {
        if (!addUnit(entry, { chapterId: "", sceneId: "", sectionKind: kind })) {
          return failed("duplicate_or_missing_unit_identity");
        }
      }
    } else {
      return failed("unsupported_section_kind");
    }
  }
  return { ok: true, chapters, scenes, units };
}

const bookWorkspace = () => ({ ok: true, stage: "book-workspace" });

/**
 * Resolve only identities from the real Children's Book projection.
 * A many-page logical Scene produces a chooser, NOT an invented 'Canon Scene'.
 * The caller owns navigation state; the existing ManuscriptPlan owns editing/saving.
 */
export function resolveChildrensBookNovelDestinationV1E3({
  navigation, chapterId = "", sceneId = "", unitId = "",
} = {}) {
  const index = readIdentityIndex(navigation);
  if (!index.ok) return index;
  const chapter = clean(chapterId);
  const scene = clean(sceneId);
  const unit = clean(unitId);
  if (chapter && !index.chapters.has(chapter)) return failed("chapter_not_found");
  if (scene && !chapter) return failed("chapter_required_for_scene");
  const sceneRecord = scene ? index.scenes.get(scene) : null;
  if (scene && (!sceneRecord || sceneRecord.chapterId !== chapter)) {
    return failed("scene_chapter_mismatch");
  }

  if (unit) {
    const page = index.units.get(unit);
    if (!page) return failed("unit_not_found");
    if (chapter && page.chapterId !== chapter) return failed("unit_chapter_mismatch");
    if (scene && page.sceneId !== scene) return failed("unit_scene_mismatch");
    return {
      ok: true,
      stage: "writer-studio",
      unitId: page.unitId,
      chapterId: page.chapterId,
      sceneId: page.sceneId,
      pageLabel: page.pageLabel,
      title: page.title,
      sourceIndex: page.sourceIndex,
      pageStart: page.pageStart,
      pageEnd: page.pageEnd,
      returnTo: page.chapterId
        ? { stage: "chapter-review", chapterId: page.chapterId }
        : { stage: "book-workspace", sectionKind: page.sectionKind },
    };
  }

  if (sceneRecord) {
    if (sceneRecord.unitIds.length === 1) {
      return resolveChildrensBookNovelDestinationV1E3({
        navigation, chapterId: chapter, sceneId: scene,
        unitId: sceneRecord.unitIds[0],
      });
    }
    return {
      ok: true,
      stage: "choose-page-or-spread",
      chapterId: chapter,
      sceneId: scene,
      choices: sceneRecord.unitIds.map((id) => {
        const entry = index.units.get(id);
        return { unitId: id, pageLabel: entry.pageLabel, title: entry.title };
      }),
      returnTo: { stage: "chapter-review", chapterId: chapter },
    };
  }

  return chapter
    ? { ok: true, stage: "chapter-review", chapterId: chapter }
    : bookWorkspace();
}

/** Continue Writing may resume a known unit; otherwise return to Book Workspace. */
export function selectChildrensBookContinueWritingV1E3({
  navigation, lastUnitId = "",
} = {}) {
  if (!clean(lastUnitId)) return resolveChildrensBookNovelDestinationV1E3({ navigation });
  const target = resolveChildrensBookNovelDestinationV1E3({ navigation, unitId: lastUnitId });
  return target.ok ? target : (readIdentityIndex(navigation).ok ? bookWorkspace() : target);
}
